import { useMemo } from 'react'
import { Link } from 'react-router'
import { Badge } from '#app/components/ui/badge.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '#app/components/ui/table.tsx'
import { formatPrice } from '#app/utils/currency.ts'
import {
	DUMPSTER_SIZES,
	matchListingToSize,
	parseDumpsterAttributes,
} from '../dumpster-size-calculator.logic.ts'

type Listing = {
	id: string
	title: string
	slug: string
	dailyRateCents: number | null
	weeklyRateCents: number | null
	monthlyRateCents: number | null
	attributes: unknown
	subcategories: Array<{ slug: string; name: string }>
	cities: Array<{ id: string; name: string; slug: string }>
	ownerBusiness: {
		name: string
		slug: string
		phone: string | null
		website: string | null
	} | null
}

export function VendorComparisonTable({
	listings,
}: {
	listings: Listing[]
}) {
	const listingsBySize = useMemo(() => {
		const grouped = new Map<string, Listing[]>()
		for (const size of DUMPSTER_SIZES) {
			grouped.set(size.subcategorySlug, [])
		}
		for (const listing of listings) {
			const sizeSlug = matchListingToSize(listing)
			if (sizeSlug) {
				grouped.get(sizeSlug)?.push(listing)
			}
		}
		return grouped
	}, [listings])

	const defaultTab =
		DUMPSTER_SIZES.find((s) => {
			const matched = listingsBySize.get(s.subcategorySlug)
			return matched && matched.length > 0
		})?.subcategorySlug ?? DUMPSTER_SIZES[0].subcategorySlug

	return (
		<section>
			<Badge variant="secondary" className="mb-3">
				Price Comparison
			</Badge>
			<h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
				Dumpster Rental Prices in Dallas-Fort Worth
			</h2>
			<p className="text-muted-foreground mt-2">
				Compare current pricing from DFW dumpster rental companies side-by-side.
				Prices reflect standard 7-day rental periods.
			</p>

			<div className="mt-8">
				<Tabs defaultValue={defaultTab}>
					<TabsList className="w-full overflow-x-auto">
						{DUMPSTER_SIZES.map((size) => {
							const count =
								listingsBySize.get(size.subcategorySlug)?.length ?? 0
							return (
								<TabsTrigger
									key={size.subcategorySlug}
									value={size.subcategorySlug}
									className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:data-[state=active]:bg-primary sm:data-[state=active]:text-primary-foreground sm:data-[state=active]:after:bg-transparent rounded-md sm:rounded-md"
								>
									{size.label}
									{count > 0 && (
										<span className="ml-1 text-xs opacity-70">
											({count})
										</span>
									)}
								</TabsTrigger>
							)
						})}
					</TabsList>

					{DUMPSTER_SIZES.map((size) => {
						const sizeListings =
							listingsBySize.get(size.subcategorySlug) ?? []

						return (
							<TabsContent
								key={size.subcategorySlug}
								value={size.subcategorySlug}
							>
								{sizeListings.length > 0 ? (
									<div className="overflow-x-auto">
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>Vendor</TableHead>
													<TableHead>Price</TableHead>
													<TableHead>Rental Period</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{sizeListings.map((listing) => {
													const attrs = parseDumpsterAttributes(
														listing.attributes,
													)
													const price =
														listing.weeklyRateCents ??
														listing.dailyRateCents
													return (
														<TableRow key={listing.id}>
															<TableCell className="font-medium">
																<span className="flex items-center gap-1.5">
																	<Icon name="building-2" size="sm" className="text-muted-foreground shrink-0" />
																	{listing.ownerBusiness ? (
																		<Link
																			to={`/businesses/${listing.ownerBusiness.slug}`}
																			className="text-primary hover:underline"
																		>
																			{listing.ownerBusiness.name}
																		</Link>
																	) : (
																		listing.title
																	)}
																</span>
															</TableCell>
															<TableCell className="font-semibold text-green-600 dark:text-green-400">
																{price
																	? formatPrice(price)
																	: 'Call for pricing'}
															</TableCell>
															<TableCell>
																<span className="flex items-center gap-1.5">
																	<Icon name="calendar" size="sm" className="text-muted-foreground" />
																	{attrs.rentalPeriodDays
																		? `${attrs.rentalPeriodDays} days`
																		: '7 days'}
																</span>
															</TableCell>
														</TableRow>
													)
												})}
											</TableBody>
										</Table>
									</div>
								) : (
									<div className="flex flex-col items-center py-12 text-center">
										<div className="bg-muted mb-4 flex size-12 items-center justify-center rounded-full">
											<Icon name="truck" size="md" className="text-muted-foreground" />
										</div>
										<p className="text-muted-foreground">
											No vendors currently listed for {size.label.toLowerCase()}{' '}
											dumpsters. Check back soon or{' '}
											<Link
												to="/rentals/category/dumpster-and-waste-rental"
												className="text-primary hover:underline"
											>
												browse all dumpster rentals
											</Link>
											.
										</p>
									</div>
								)}
							</TabsContent>
						)
					})}
				</Tabs>
			</div>
		</section>
	)
}
